<template>
  <div class="flex flex-col gap-4">
    <div class="sticky top-0 z-10 bg-white">
      <h1 class="text-left text-xl font-medium text-primary">
        Topics
      </h1>

      <section class="mx-auto mt-4 w-[98%]">
        <InputWithIcon
          :val="topicQuery"
          classes="py-2 text-sm rounded-lg"
          placeholder="Search topics..."
          @input="topicQuery = $event"
        >
          <template #icon>
            <SearchIcon />
          </template>
        </InputWithIcon>
      </section>
      <!-- Selected topics summary -->
      <section class="mt-4 flex items-center gap-3.5 text-sm">
        <div class="flex items-center gap-1.5">
          <input
            id="select-all-topics-checkbox"
            ref="select-all-topics-checkbox"
            type="checkbox"
            class="cursor-pointer rounded-sm border-2 border-gray-400 bg-white p-1.5 text-main-primary hover:border-gray-500 focus:border-main-primary focus:ring-transparent"
            @change="toggleCheckAllTopics($event)"
          >
          <label
            for="select-all-topics-checkbox"
            class="text-[#68737D]"
          >Select All</label>
        </div>
        <span class="text-[#68737D]">{{ selectedTopics.length }} Selected</span>
      </section>
    </div>
    <section class="grow">
      <ul
        v-if="filteredThemeTopics.length"
        class="flex w-full flex-col gap-3"
      >
        <li
          v-for="({ theme, isExpanded }, index) in filteredThemeTopics"
          :key="`theme-${index}`"
        >
          <div
            class="flex items-center justify-between gap-2 font-medium marker:content-none hover:cursor-pointer"
          >
            <div class="flex items-center gap-1.5">
              <input
                :id="`check-${theme}`"
                v-model="filteredThemeTopics[index].checked"
                type="checkbox"
                class="cursor-pointer rounded-sm border-2 border-gray-400 bg-white p-1.5 text-main-primary hover:border-gray-500 focus:border-main-primary focus:ring-transparent"
                @change="toggleCheckAllTopicsByTheme($event, index)"
              >
              <label class="text-[#68737D]">{{ theme }}</label>
            </div>
            <button
              :id="`button-${theme}`"
              type="button"
              :title="isExpanded ? 'Hide' : 'Expand'"
              class="font-inter rounded bg-transparent px-4 py-1 text-gray-500"
              @click="toggleTopics(index)"
            >
              <ChevronDownLarge
                :custom-style="`h-5 w-5 ${isExpanded ? 'rotate-180' : ''}`"
              />
            </button>
          </div>
          <ul
            v-if="filteredThemeTopics[index].isExpanded"
            class="whitespace-nowrap px-6"
          >
            <li
              v-for="({ title }, topicIndex) in filteredThemeTopics[index]
                .theme_items"
              :key="`theme-${index}-topic-${topicIndex}`"
              class="esg-topic-item"
            >
              <input
                v-model="
                  filteredThemeTopics[index].theme_items[topicIndex].checked
                "
                type="checkbox"
                class="cursor-pointer rounded-sm border-2 border-gray-400 bg-white p-1.5 text-main-primary hover:border-gray-500 focus:border-main-primary focus:ring-transparent"
                @change="toggleCheckTopic($event, index, topicIndex)"
              >
              {{ title }}
            </li>
          </ul>
        </li>
      </ul>

      <div
        v-else
        class="flex h-full w-full items-center justify-center rounded border-2 border-dashed border-[#D4D4D4] bg-[#F8FAFC] px-2 py-16 text-center"
      >
        <span class="text-sm text-gray-400">Select Framework(s) to view Topics
        </span>
      </div>
    </section>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { savedTopicsInjectionKey } from '@/utils/keys'

export default {
  name: 'EsgTopicsPicker',

  inject: { savedTopicsInjectionKey },

  props: {
    themeTopics: {
      type: Array,
      required: true,
    },
    savedTopics: {
      type: Array,
      default() {
        return []
      },
    },
  },

  emits: ['topicsSelected'],

  data() {
    return {
      topicQuery: '',
      filteredThemeTopics: this.transformThemeTopics(this.themeTopics),
      allThemeTopics: this.transformThemeTopics(this.themeTopics),
      selectedTopics: [],
      alreadySavedTopics: this.savedTopics.length
        ? this.savedTopics
        : this.savedTopicsInjectionKey,
    }
  },

  watch: {
    topicQuery(newQuery, oldQuery) {
      if (!oldQuery && newQuery) {
        // First time searching, set the checked items in the clean allThemeTopics
        this.allThemeTopics = this.filteredThemeTopics
      }
      if (!newQuery) {
        this.filteredThemeTopics = this.allThemeTopics
      }
      else {
        this.filteredThemeTopics = [...this.allThemeTopics].filter(
          ({ theme, theme_items }) =>
            theme.toLowerCase().includes(newQuery.toLowerCase())
            || theme_items.some(({ title }) =>
              title.toLowerCase().includes(newQuery.toLowerCase()),
            ),
        )
      }
    },
    themeTopics: {
      handler: debounce(function (newThemeTopics, oldThemeTopics) {
        if (!newThemeTopics.length && oldThemeTopics.length) {
          this.alreadySavedTopics = []
        }

        const filteredThemeTopics = this.transformThemeTopics(
          newThemeTopics,
          !oldThemeTopics.length,
        )

        const selectedTopics = []

        if (oldThemeTopics.length) {
        // Check previously checked topics after toggling versions: updates selectedTopics
          filteredThemeTopics.forEach((themeTopic, index) => {
            themeTopic.theme_items.forEach((topic) => {
              const topicCopy = {
                ...topic,
                theme: filteredThemeTopics[index].theme,
              }
              const isPreviouslySelected
              = this.selectedTopics.findIndex(({ id }) => id === topicCopy.id)
              !== -1

              if (topicCopy.checked && isPreviouslySelected) {
                delete topicCopy.checked
                selectedTopics.push(topicCopy)
              }
            })
          })
        }

        // Replace instead of mutate
        this.selectedTopics = [...selectedTopics]
        this.filteredThemeTopics = [...filteredThemeTopics]
        this.allThemeTopics = [...filteredThemeTopics]

        if (!oldThemeTopics.length) {
          const updatedSelectedTopics = [...this.selectedTopics]

          // Check from the saved API data only the first visit: updates selectedTopics
          this.filteredThemeTopics.forEach((themeTopic, index) => {
            themeTopic.theme_items.forEach((topic) => {
              const topicIndex = this.selectedTopics.findIndex(
                ({ id }) => id === topic.id,
              )

              if (
                topicIndex === -1
                && this.alreadySavedTopics.includes(topic.id)
              ) {
                const topicCopy = {
                  ...topic,
                  theme: this.filteredThemeTopics[index].theme,
                }

                delete topicCopy.checked
                updatedSelectedTopics.push(topicCopy)
              }
            })
          })

          // Replace instead of mutating
          this.selectedTopics = [...updatedSelectedTopics]
        }

        nextTick(() => this.updateSelectAllChecker())
      }, 0),
    },
    selectedTopics: {
      deep: true,
      handler(newSelectedTopics) {
        this.$emit('topicsSelected', newSelectedTopics)
      },
    },
  },

  mounted() {
    nextTick(() => this.updateSelectAllChecker())
  },

  methods: {
    transformThemeTopics(themeTopics, firstVisit = true) {
      return [...themeTopics]
        .filter(({ theme_items }) => theme_items.length)
        .map((theme) => {
          theme.isExpanded = true
          theme.theme_items = theme.theme_items.map((topic) => {
            const isPreviouslySelected
              = this.selectedTopics?.findIndex(({ id }) => id === topic.id)
              !== -1
            topic.checked = firstVisit
              ? this.alreadySavedTopics?.includes(topic.id)
              : isPreviouslySelected
            return topic
          })
          theme.checked = theme.theme_items.every(({ checked }) => checked)
          return theme
        })
    },
    toggleCheckAllTopics(event) {
      if (this.topicQuery) {
        this.transformDataForAllTopics(this.allThemeTopics, event)
      }

      this.transformDataForAllTopics(this.filteredThemeTopics, event)
    },
    transformDataForAllTopics(list, event) {
      list = list.map((themeTopic, index) => {
        themeTopic.checked = event.target.checked
        themeTopic.theme_items = themeTopic.theme_items.map((topic) => {
          topic.checked = event.target.checked
          topic.theme = list[index].theme
          const toggledTopic = { ...topic }
          const toggledTopicIndex = this.selectedTopics.findIndex(
            ({ id }) => id === toggledTopic.id,
          )

          if (event.target.checked && toggledTopicIndex === -1) {
            delete toggledTopic.checked
            this.selectedTopics.push(toggledTopic)
          }
          else if (!event.target.checked && toggledTopicIndex !== -1) {
            this.selectedTopics.splice(toggledTopicIndex, 1)
          }
          return topic
        })
        return themeTopic
      })
    },
    toggleCheckAllTopicsByTheme(event, index) {
      if (this.topicQuery) {
        // Find the corresponding themeIndex in allThemeTopics
        const allDataThemeIndex = this.allThemeTopics.findIndex(
          ({ theme }) => theme === this.filteredThemeTopics[index].theme,
        )

        this.transformDataForTopicsByTheme(
          this.allThemeTopics,
          event,
          allDataThemeIndex,
        )
      }

      this.transformDataForTopicsByTheme(
        this.filteredThemeTopics,
        event,
        index,
      )

      this.updateSelectAllChecker()
    },
    transformDataForTopicsByTheme(list, event, index) {
      list[index].theme_items = list[index].theme_items.map((topic) => {
        topic.checked = event.target.checked

        this.updateSelectedTopics(event, { ...topic }, index, list)

        return topic
      })
    },
    toggleCheckTopic(event, themeIndex, topicIndex) {
      if (this.topicQuery) {
        // Find the corresponding themeIndex & topicIndex in allThemeTopics
        const allDataThemeIndex = this.allThemeTopics.findIndex(
          ({ theme }) => theme === this.filteredThemeTopics[themeIndex].theme,
        )
        const { id: topicId }
          = this.filteredThemeTopics[themeIndex].theme_items[topicIndex]
        const allDataTopicIndex = this.allThemeTopics[
          allDataThemeIndex
        ].theme_items.findIndex(({ id }) => id === topicId)

        this.transformDataForCheckedTopic(
          this.allThemeTopics,
          event,
          allDataThemeIndex,
          allDataTopicIndex,
        )
      }

      this.transformDataForCheckedTopic(
        this.filteredThemeTopics,
        event,
        themeIndex,
        topicIndex,
      )

      this.updateSelectAllChecker()
    },
    updateSelectAllChecker() {
      if (!this.topicQuery) {
        nextTick(() => {
          if (this.$refs['select-all-topics-checkbox']) {
            this.$refs['select-all-topics-checkbox'].checked = !this
              .filteredThemeTopics.length
              ? false
              : this.filteredThemeTopics.every(({ checked }) => checked)
          }
        })
      }
    },
    transformDataForCheckedTopic(list, event, themeIndex, topicIndex) {
      list[themeIndex].theme_items[topicIndex].checked = event.target.checked
      const toggledTopic = { ...list[themeIndex].theme_items[topicIndex] }

      this.updateSelectedTopics(event, toggledTopic, themeIndex, list)

      list[themeIndex].checked = list[themeIndex].theme_items.every(
        ({ checked }) => checked,
      )
    },
    updateSelectedTopics(event, toggledTopic, themeIndex, list) {
      const toggledTopicIndex = this.selectedTopics.findIndex(
        ({ id }) => id === toggledTopic.id,
      )

      if (event.target.checked && toggledTopicIndex === -1) {
        delete toggledTopic.checked
        toggledTopic.theme = list[themeIndex].theme
        this.selectedTopics.push(toggledTopic)
      }
      else if (!event.target.checked && toggledTopicIndex !== -1) {
        this.selectedTopics.splice(toggledTopicIndex, 1)
      }
    },
    toggleTopics(index) {
      this.filteredThemeTopics[index].isExpanded
        = !this.filteredThemeTopics[index].isExpanded
      getCurrentInstance()?.proxy?.$forceUpdate()
    },
  },
}
</script>
